// src/prototype/components/sep/SEPSectionResourcePlan.tsx

import React from 'react';
import { Users } from 'lucide-react';
import { effortTierForBadge } from '../../config/opportunityBadge';

interface Finalist {
  id: string;
  title: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  category: string;
}

export interface SEPSectionResourcePlanProps {
  finalists: Finalist[];
  clientName: string;
}

interface RoleRow { role: string; team: string; fte: string; note: string }

const getRolesByEffort = (effort: string): RoleRow[] => {
  if (effort === 'Low') return [
    { role: 'Data Analyst', team: 'Analytics', fte: '0.5 FTE', note: 'Data prep & dashboard build' },
    { role: 'Project Manager', team: 'PMO', fte: '0.25 FTE', note: 'Coordination & sponsor comms' },
    { role: 'Clinical Champion', team: 'Clinical Ops', fte: '0.1 FTE', note: 'Workflow validation' },
  ];
  if (effort === 'High') return [
    { role: 'Data Engineer', team: 'Data Foundation', fte: '1.0 FTE', note: 'Pipeline build & data integration' },
    { role: 'Data Scientist', team: 'Analytics', fte: '0.75 FTE', note: 'Model development & validation' },
    { role: 'Project Manager', team: 'PMO', fte: '0.5 FTE', note: 'Program management' },
    { role: 'Clinical Champion', team: 'Clinical Ops', fte: '0.25 FTE', note: 'Clinical workflow design' },
    { role: 'IT Architect', team: 'IT', fte: '0.5 FTE', note: 'Infrastructure & security' },
    { role: 'Change Manager', team: 'Org Dev', fte: '0.25 FTE', note: 'Adoption & training' },
  ];
  return [
    { role: 'Data Analyst', team: 'Analytics', fte: '0.75 FTE', note: 'Analytics development' },
    { role: 'Data Engineer', team: 'Data Foundation', fte: '0.5 FTE', note: 'Data integration' },
    { role: 'Project Manager', team: 'PMO', fte: '0.5 FTE', note: 'Program coordination' },
    { role: 'Clinical Champion', team: 'Clinical Ops', fte: '0.25 FTE', note: 'Workflow design' },
  ];
};

const effortColor = (e: string) => ({ Low: '#059669', Medium: '#D97706', High: '#DC2626' })[e] ?? '#717182';
const effortBg = (e: string) => ({ Low: 'bg-emerald-50 border-emerald-100', Medium: 'bg-amber-50 border-amber-100', High: 'bg-rose-50 border-rose-100' })[e] ?? 'bg-gray-50 border-gray-200';

export const SEPSectionResourcePlan: React.FC<SEPSectionResourcePlanProps> = ({ finalists, clientName }) => (
  <div className="bg-white border border-[#e5e7eb] rounded-xl p-6 mb-6">
    <div className="flex items-start gap-3 mb-6">
      <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
      <div>
        <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 6</p>
        <h2 className="text-lg font-bold text-[#18365E] leading-tight">Resource & Capacity Plan</h2>
        <p className="text-[11px] text-[#717182] mt-0.5">FTE demand by role across the initiative portfolio</p>
      </div>
    </div>

    <p className="text-sm text-[#18365E]/80 leading-relaxed mb-6">
      The resource plan below outlines the estimated FTE requirements for each initiative at <strong>{clientName}</strong>.
      Staffing models are derived from initiative complexity and scope, and assume engagement of existing internal teams supplemented by targeted external expertise where noted.
    </p>

    <div className="space-y-5">
      {finalists.map((f, idx) => {
        // Resourcing keys on the ROOT-derived effort tier, not the stored string.
        const roles = getRolesByEffort(effortTierForBadge({ id: f.id, implementationEffort: f.implementationEffort }));
        const totalFte = roles.reduce((acc, r) => acc + parseFloat(r.fte), 0).toFixed(2);
        return (
          <div key={f.id} className="border border-[#e5e7eb] rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-[#F5F7FA] border-b border-[#e5e7eb]">
              <div className="flex items-center gap-2.5">
                <div className="w-6 h-6 rounded-full bg-[#18365E] flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white">{idx + 1}</span>
                </div>
                <p className="text-sm font-semibold text-[#18365E]">{f.title}</p>
              </div>
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border ${effortBg(f.implementationEffort)}`} style={{ color: effortColor(f.implementationEffort) }}>
                <Users className="w-3 h-3" />
                {totalFte} FTE Total
              </div>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#e5e7eb]">
                  <th className="text-left px-4 py-2 font-semibold text-[#717182]">Role</th>
                  <th className="text-left px-3 py-2 font-semibold text-[#717182]">Team</th>
                  <th className="text-center px-3 py-2 font-semibold text-[#717182]">FTE</th>
                  <th className="text-left px-3 py-2 font-semibold text-[#717182]">Scope</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((r, ri) => (
                  <tr key={ri} className={ri < roles.length - 1 ? 'border-b border-[#e5e7eb]/60' : ''}>
                    <td className="px-4 py-2.5 font-medium text-[#18365E]">{r.role}</td>
                    <td className="px-3 py-2.5 text-[#717182]">{r.team}</td>
                    <td className="px-3 py-2.5 text-center font-semibold text-[#18365E]">{r.fte}</td>
                    <td className="px-3 py-2.5 text-[#717182]">{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>

    <div className="mt-5 bg-[#F5F7FA] border border-[#e5e7eb] rounded-xl p-4">
      <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">Capacity Planning Notes</p>
      <div className="space-y-1.5">
        {[
          'FTE estimates represent average engagement across the initiative timeline, not peak sprint demand.',
          'Quick Win initiatives can typically leverage existing team capacity with minimal backfill requirements.',
          'Complex initiatives may require phased staffing ramp to align with delivery milestones.',
          'External vendor or consulting support should be scoped for initiatives exceeding 1.5 FTE demand.',
        ].map((note, i) => (
          <div key={i} className="flex items-start gap-2">
            <div className="w-1 h-1 rounded-full bg-[#FF6E4C] mt-1.5 shrink-0" />
            <p className="text-xs text-[#18365E]/75 leading-relaxed">{note}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default SEPSectionResourcePlan;